import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { REMOTE__images_file_data_upload_to_workspace } from '@/state/images/upload/REMOTE__images_file_data_upload_to_workspace';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { file_data_from_canvas_offscreen } from '@/utils/UTILS__image';
import { assert_array_has_1_item, assert_false, assert_instanceof, assert_not_null, assert_type_array } from '@shared/asserts/utils_asserts';
import { DTO_REQUEST__images_upload } from '@shared/dto/+image/DTO_REQUEST__images_upload';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { DTO_RESPONSE__error } from '@shared/network/DTO_RESPONSE__error';
import { useState } from 'react';
import { HOOK__get_div_size } from '../../../_hooks/HOOK__get_div_size';
import { HOOK_CANVAS__p5 } from '../../_base/p5/HOOK_CANVAS__p5';
import { G_IMAGE__master_component } from '../../nodes/node/fields/image/_views/G_IMAGE__master_component';
import { ENUM__image_brush } from '../_base/ENUM__image_brush';
import { G_LAYOUT__image_edit_base } from '../_base/G_LAYOUT__image_edit_base';
import { WATCHER__change_brush_size_by_scroll } from '../_base/WATCHER__change_brush_size_by_scroll';
import { G_CURSOR__brush_draw } from '../tab_mask/_cursors/G_CURSOR__brush_draw';

export function G_LAYOUT__IMAGE_EDIT__line_draw() {
  /*
   * STORE
   */
  const dispatch = use_dispatch();

  const { generation_current } = use_slice_generation(['generation_current']);
  const { workspace_current } = use_slice_workspace(['workspace_current']);
  const {
    g_image_edit__brush,
    g_general__ui_disabled,
    g_image_edit__field: field,
  } = use_slice_ui(['g_image_edit__brush', 'g_general__ui_disabled', 'g_image_edit__field']);

  /*
   * STATE
   */

  const [is_hovered, set_is_hovered] = useState(false);

  const { div_element, ref_element__set, size: container_size } = HOOK__get_div_size();

  HOOK_CANVAS__p5({ div_element, container_size, image: field.asset, callback__apply_changes: apply_changes });

  /*
   * EVENTS
   */

  async function apply_changes(ref_canvas: React.MutableRefObject<HTMLCanvasElement>) {
    assert_instanceof(field, FIELD__image_asset);
    assert_false(field.is_stub());

    let { asset: image_asset } = field;

    let { width, height } = image_asset.image_size;

    let canvas_offscreen = new OffscreenCanvas(width, height);

    const context_destination = canvas_offscreen.getContext('2d');
    assert_not_null(context_destination);

    context_destination.imageSmoothingEnabled = false;
    context_destination.drawImage(ref_canvas.current, 0, 0, ref_canvas.current.width, ref_canvas.current.height, 0, 0, width, height);

    let file_data = await file_data_from_canvas_offscreen(canvas_offscreen);

    let request_upload = new DTO_REQUEST__images_upload();
    request_upload.category = field.category;

    request_upload.file_data_list = [file_data];
    request_upload.workspace_id = workspace_current._id;

    let action_result = await dispatch(REMOTE__images_file_data_upload_to_workspace(request_upload));

    let response = action_result.payload;

    if (response instanceof DTO_RESPONSE__error) return;

    assert_type_array(response);
    let image = assert_array_has_1_item(response);

    assert_instanceof(image, DTO__image_asset);

    let field_new = field.clone();
    field_new.image__asset_use(image);

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);
  }

  /*
   * METHODS
   */

  /*
   * HTML
   */

  if (!field.image__is_initialized) return <div className="size-full bg-bar flex-center text-bar-foreground">No image</div>;

  return (
    <G_LAYOUT__image_edit_base>
      <div onMouseLeave={() => set_is_hovered(false)} onMouseEnter={() => set_is_hovered(true)} className="contents">
        {is_hovered && <WATCHER__change_brush_size_by_scroll />}

        <G_IMAGE__master_component
          field={field}
          draggable={true}
          class_name_image={`${g_image_edit__brush == ENUM__image_brush.POINTER || g_general__ui_disabled ? 'cursor-default' : 'cursor-none pointer-events-none'}`}
        >
          <div ref={ref_element__set} className={`absolute top-0 size-full cursor-none pointer-events-none`} />
          <G_CURSOR__brush_draw />
        </G_IMAGE__master_component>
      </div>
    </G_LAYOUT__image_edit_base>
  );
}
